import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

// India-only phone input. The "+91" + flag sits in a fixed pill on the left
// and the user only types the 10 national digits — we prepend "+91" on the way
// up to the parent. Parent receives either "+91XXXXXXXXXX" or "" (empty).
// Backend regex is `^\+?[0-9]{10,15}$`; we always emit the `+` form so
// formatting quirks can't break validation.

const DIAL = '+91';

// Convert the canonical value prop back into the 10 national digits for
// display. The component always *emits* "+91XXXXXXXXXX", so we strip that
// literal prefix. We also accept other common inbound shapes (bare digits,
// "919876543210", "+91 98765 43210") so external callers don't have to
// pre-normalize.
function nationalDigits(value: string): string {
  if (!value) return '';
  if (value.startsWith('+91')) {
    return value.slice(3).replace(/\D/g, '').slice(0, 10);
  }
  const digits = value.replace(/\D/g, '');
  // "91XXXXXXXXXX" with country code present: drop it.
  if (digits.length > 10 && digits.startsWith('91')) return digits.slice(-10);
  return digits.slice(0, 10);
}

function formatForDisplay(digits: string): string {
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)} ${digits.slice(5, 10)}`;
}

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
};

export const PhoneInput = forwardRef<HTMLInputElement, Props>(function PhoneInput(
  {
    value,
    onChange,
    placeholder = '98765 43210',
    disabled,
    className,
    autoFocus,
    required,
    id,
    name,
  },
  ref,
) {
  const display = formatForDisplay(nationalDigits(value));

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    // The user is typing into the national portion only — the "+91" pill is a
    // fixed prefix, not part of the input value. Keep just the first 10 digits
    // of whatever the user typed or pasted.
    const national = e.target.value.replace(/\D/g, '').slice(0, 10);
    onChange(national ? `${DIAL}${national}` : '');
  }

  return (
    <div
      className={cn(
        'flex w-full items-stretch overflow-hidden rounded-md border border-gray-300 bg-white focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-500/30',
        disabled && 'bg-gray-50 opacity-60',
        className,
      )}
    >
      <span
        aria-hidden
        className="flex shrink-0 items-center gap-1 border-r border-gray-200 bg-gray-50 px-3 text-sm text-gray-700"
      >
        <span className="text-base leading-none">🇮🇳</span>
        <span className="font-medium">+91</span>
      </span>
      <input
        ref={ref}
        id={id}
        name={name}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        pattern="[0-9 ]*"
        placeholder={placeholder}
        value={display}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        maxLength={11}
        className="w-full border-0 bg-transparent px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
      />
    </div>
  );
});

// Helper for callers that want to validate before submit.
export function isValidIndianMobile(e164: string): boolean {
  return /^\+91[0-9]{10}$/.test(e164);
}

// Normalize any stored form ("+919876543210", "919876543210", "9876543210",
// "+91 98765 43210", etc.) into the canonical "+91XXXXXXXXXX" string this
// component emits. Returns an empty string if the input doesn't contain a
// recognizable 10-digit Indian number — so `dirty` / validity comparisons
// stay consistent regardless of whatever the server happens to send back.
export function normalizeIndianMobile(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const national = digits.length > 10 && digits.startsWith('91') ? digits.slice(-10) : digits;
  return national.length === 10 ? `+91${national}` : '';
}
